import React from 'react';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { msalInstance } from './config/msalInstance';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/auth/LoginPage';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './pages/Dashboard';
import CVRedactor from './features/cv-redactor/CVRedactor';
import Enhancer from './features/enhancer/Enhancer';
import ProposalGenerator from './features/proposal-generator/ProposalGenerator';
import BatchProfiles from './features/batch-profiles/BatchProfiles';
import ProfilePictureManager from './features/profile-picture-manager/ProfilePictureManager';
import ProjectTracker from './features/project-tracker';
import ContractorProfile from './features/contractor-profile/ContractorProfile';
import { features, Feature } from './config/features';
import './styles/global.css';
import RouteGuard from './components/access/RouteGuard';
import Forbidden from './pages/Forbidden';

const featuresById: Record<string, Feature> = features.reduce((acc, feature) => {
  acc[feature.id] = feature;
  return acc;
}, {} as Record<string, Feature>);

// Wrapper component for layout
const RouteWrapper: React.FC<{ children: React.ReactNode; availableFeatures: Feature[] }> = ({ children, availableFeatures }) => {
  // No sidebar logic needed - each feature manages its own
  return (
    <AppLayout features={availableFeatures}>
      {children}
    </AppLayout>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, hasAnyGroup } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const accessibleFeatures = features.filter(feature => hasAnyGroup(feature.requiredGroups));

  return (
    <Router>
      <RouteWrapper availableFeatures={accessibleFeatures}>
        <Routes>
          <Route path="/" element={<Dashboard features={accessibleFeatures} />} />
          <Route
            path="/cv-redactor"
            element={(
              <RouteGuard requiredGroups={featuresById['cv-redactor']?.requiredGroups}>
                <CVRedactor />
              </RouteGuard>
            )}
          />
          <Route
            path="/enhancer"
            element={(
              <RouteGuard requiredGroups={featuresById['enhancer']?.requiredGroups}>
                <Enhancer />
              </RouteGuard>
            )}
          />
          <Route
            path="/proposal-generator"
            element={(
              <RouteGuard requiredGroups={featuresById['proposal-generator']?.requiredGroups}>
                <ProposalGenerator />
              </RouteGuard>
            )}
          />
          <Route
            path="/batch-profiles"
            element={(
              <RouteGuard requiredGroups={featuresById['batch-profiles']?.requiredGroups}>
                <BatchProfiles />
              </RouteGuard>
            )}
          />
          <Route
            path="/profile-pictures"
            element={(
              <RouteGuard requiredGroups={featuresById['profile-pictures']?.requiredGroups}>
                <ProfilePictureManager />
              </RouteGuard>
            )}
          />
          <Route
            path="/contractor-profile"
            element={<ContractorProfile />}
          />
          <Route
            path="/projects/*"
            element={(
              <RouteGuard requiredGroups={featuresById['project-tracker']?.requiredGroups}>
                <ProjectTracker />
              </RouteGuard>
            )}
          />
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteWrapper>
    </Router>
  );
};

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
